<template>
    <div class="sticky-header-manager"></div>
</template>

<script>
export default {
    props: {
        scrollThreshold: {
            type: Number,
            default: 100
        },
        title: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            isScrolled: false,
            scrollY: 0,
            header: null,
            spacer: null,
            resizeObserver: null,
            mutationObserver: null,
            initialHeight: 0,
            scrolledHeight: 0,
            ticking: false,
            isMobile: false,
            isReducedMotion: false,
            titleElement: null,
            dynamicTitle: this.title
        }
    },
    watch: {
        title(newTitle) {
            this.updateDynamicTitle(newTitle)
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.detectMobile()
            this.detectReducedMotion()
            this.initStickyHeader()
            this.setupEventListeners()
        })
    },
    beforeUnmount() {
        this.removeEventListeners()
        if (this.resizeObserver) {
            this.resizeObserver.disconnect()
        }
        if (this.mutationObserver) {
            this.mutationObserver.disconnect()
        }
        if (this.titleElement && this.titleElement.parentNode) {
            this.titleElement.parentNode.removeChild(this.titleElement)
        }
    },
    methods: {
        setupEventListeners() {
            window.addEventListener('scroll', this.handleScroll, { passive: true })
            window.addEventListener('resize', this.handleResize, { passive: true })
        },

        removeEventListeners() {
            window.removeEventListener('scroll', this.handleScroll)
            window.removeEventListener('resize', this.handleResize)
        },
        updateDynamicTitle(newTitle) {
            if (newTitle && newTitle !== this.dynamicTitle) {
                this.dynamicTitle = newTitle

                // Créer l'élément titre s'il n'existe pas encore
                if (!this.titleElement && this.dynamicTitle) {
                    this.createTitleElement()
                } else if (this.titleElement) {
                    this.updateTitleElement()
                }

                // Émettre un événement pour informer d'autres composants
                this.$emit('title-changed', newTitle)
            }
        },

        updateTitleElement() {
            if (this.titleElement && this.dynamicTitle) {
                this.titleElement.innerHTML = `<h1 class="text-primary text-lg font-semibold text-center">${this.dynamicTitle}</h1>`
            }
        },

        detectMobile() {
            this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                window.innerWidth <= 768 ||
                ('ontouchstart' in window)
        },

        detectReducedMotion() {
            // Respect des préférences d'accessibilité
            this.isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        },

        initStickyHeader() {
            this.header = document.querySelector('header')
            if (!this.header) return

            this.applyStyles()
            // Force un reflow pour s'assurer que les styles sont appliqués
            this.header.offsetHeight
            this.calculateHeaderHeights()
            this.header.classList.add('sticky-header-component')

            // Créer l'élément titre seulement si on a un titre
            if (this.dynamicTitle) {
                this.createTitleElement()
            }

            this.$nextTick(() => {
                this.createSpacer()
                this.setupResizeObserver()
            })
        },

        createTitleElement() {
            const mainMenu = document.querySelector('#main-menu .swiper-wrapper')
            if (!mainMenu) return

            // Créer l'élément titre seulement si on a un titre
            if (this.dynamicTitle) {
                this.titleElement = document.createElement('div')
                this.titleElement.className = 'mag-title'
                this.updateTitleElement()
                mainMenu.appendChild(this.titleElement)
            }
        },

        calculateHeaderHeights() {
            if (!this.header) return

            const clone = this.header.cloneNode(true)
            clone.style.position = 'absolute'
            clone.style.visibility = 'hidden'
            clone.style.top = '-9999px'
            clone.style.left = '0'
            clone.style.right = '0'
            clone.style.width = this.header.offsetWidth + 'px'

            document.body.appendChild(clone)
            clone.classList.add('sticky-header-component')

            this.initialHeight = this.getComputedHeight(clone)

            clone.classList.add('scrolled')
            this.scrolledHeight = this.getComputedHeight(clone)

            document.body.removeChild(clone)
        },

        getComputedHeight(element) {
            const styles = window.getComputedStyle(element)
            const height = element.offsetHeight
            const marginTop = parseInt(styles.marginTop) || 0
            const marginBottom = parseInt(styles.marginBottom) || 0
            return height + marginTop + marginBottom
        },

        createSpacer() {
            this.spacer = document.createElement('div')
            this.spacer.classList.add('header-spacer')
            this.spacer.style.height = `${this.initialHeight}px`
            this.header.parentNode.insertBefore(this.spacer, this.header)
        },

        updateSpacerHeight() {
            if (this.header && this.spacer) {
                const targetHeight = this.isScrolled ? this.scrolledHeight : this.initialHeight
                // Utilisation de transform au lieu de height pour de meilleures performances
                requestAnimationFrame(() => {
                    this.spacer.style.height = `${targetHeight}px`
                })
            }
        },

        setupResizeObserver() {
            let resizeTimeout
            this.resizeObserver = new ResizeObserver(() => {
                clearTimeout(resizeTimeout)
                resizeTimeout = setTimeout(() => {
                    this.calculateHeaderHeights()
                    this.updateSpacerHeight()
                }, 100)
            })
            this.resizeObserver.observe(this.header)
        },

        handleScroll() {
            if (!this.ticking) {
                requestAnimationFrame(() => {
                    this.scrollY = window.scrollY
                    const newIsScrolled = this.scrollY > this.scrollThreshold

                    if (newIsScrolled !== this.isScrolled) {
                        this.isScrolled = newIsScrolled
                        if (this.header) {
                            this.header.classList.toggle('scrolled', this.isScrolled)

                            if (this.dynamicTitle) {
                                this.toggleTitleDisplay(this.isScrolled)
                            }

                            this.$nextTick(() => {
                                this.updateSpacerHeight()
                            })
                        }
                    }
                    this.ticking = false
                })
                this.ticking = true
            }
        },

        toggleTitleDisplay(isScrolled) {
            const swiperSlides = document.querySelectorAll('#main-menu .swiper-slide:not(:first-child)')
            const swiperContainer = document.querySelector('.swiper-container--with-arrow')

            if (isScrolled && this.dynamicTitle) {
                swiperSlides.forEach(slide => {
                    slide.classList.add('hidden-for-title')
                })
                // Vider temporairement le contenu du pseudo-élément after
                if (swiperContainer) {
                    swiperContainer.classList.add('hide-arrow-content')
                }
                if (this.titleElement) {
                    this.titleElement.classList.add('visible')
                }
            } else {
                swiperSlides.forEach(slide => {
                    slide.classList.remove('hidden-for-title')
                })
                // Rétablir le contenu du pseudo-élément after
                if (swiperContainer) {
                    swiperContainer.classList.remove('hide-arrow-content')
                }
                if (this.titleElement) {
                    this.titleElement.classList.remove('visible')
                }
            }
        },

        handleResize() {
            const debounceTime = this.isMobile ? 250 : 150
            if (this.resizeTimeout) {
                clearTimeout(this.resizeTimeout)
            }
            this.resizeTimeout = setTimeout(() => {
                this.detectMobile()
                this.calculateHeaderHeights()
                this.updateSpacerHeight()
            }, debounceTime)
        },

        applyStyles() {
            const transitionDuration = this.isReducedMotion ? '0s' : (this.isMobile ? '0.25s' : '0.35s')
            const transitionEasing = this.isMobile ? 'ease-out' : 'cubic-bezier(0.4, 0, 0.2, 1)'

            const styles = `
                :root {
                    --header-transition-duration: ${transitionDuration};
                    --header-transition-easing: ${transitionEasing};
                    --header-transition: var(--header-transition-duration) var(--header-transition-easing);
                }

                .sticky-header-component {
                    position: fixed !important;
                    top: 0;
                    left: 0;
                    right: 0;
                    z-index: 1000;
                    background: white;
                    transition: all var(--header-transition);
                    will-change: transform, opacity;
                    transform: translateZ(0);
                    -webkit-transform: translateZ(0);
                    backface-visibility: hidden;
                    -webkit-backface-visibility: hidden;
                    perspective: 1000px;
                    -webkit-perspective: 1000px;
                }

                .header-spacer {
                    width: 100%;
                    background: transparent;
                    height: 0px;
                    transition: height var(--header-transition);
                    contain: layout style;
                }

                .swiper-container--with-arrow.hide-arrow-content:after {
                    content: '' !important;
                }
                .mag-title {
                    position: absolute;
                    top: 0;
                    left: 50px;
                    bottom: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    opacity: 0;
                    visibility: hidden;
                    transition: opacity var(--header-transition), visibility var(--header-transition);
                    pointer-events: none;
                    background: white;
                    z-index: 10;
                }

                .mag-title.visible {
                    opacity: 1;
                    visibility: visible;
                    pointer-events: auto;
                }

                .swiper-slide.hidden-for-title {
                    opacity: 0;
                    visibility: hidden;
                    display: none;
                    transition: opacity var(--header-transition), visibility var(--header-transition);
                }

                #main-menu .swiper-wrapper {
                    position: relative;
                }

                @media (max-width: 768px) {
                    .sticky-header-component {
                        transition: transform var(--header-transition), opacity var(--header-transition);
                    }

                    .mag-title h1 {
                        font-size: 16px;
                        line-height: 15px;
                    }

                    @media (prefers-reduced-motion: reduce) {
                        .sticky-header-component,
                        .header-spacer,
                        .mag-title,
                        .swiper-slide.hidden-for-title {
                            transition: none !important;
                        }
                    }
                }
            `
            const styleSheet = document.createElement('style')
            styleSheet.textContent = styles
            document.head.appendChild(styleSheet)
        }
    }
}
</script>

<style scoped>
.sticky-header-manager {
    position: relative;
}

/* Variables globales pour les transitions harmonisées */
:global(:root) {
    --header-transition-duration: 0.25s;
    --header-transition-easing: ease-out;
    --header-transition: var(--header-transition-duration) var(--header-transition-easing);
}

/* Positionnement des dropdowns */
:global(.sticky-header-component.scrolled .menu-mobile),
:global(.sticky-header-component.scrolled #dropdown-menu) {
    top: 36px;
}

/* masquer titre de l edition (talent/quotidien) en dehors scroll */
:global(.sticky-header-component .menu-horizontal-force-title .horizontal-container) {
    display: none;
}

:global(.sticky-header-component.scrolled .menu-horizontal-force-title .horizontal-container) {
    display: flex;
}

/* === LOGO === */
:global(.sticky-header-component .header__logo) {
    transition: transform var(--header-transition);
    transform-origin: center;
    will-change: transform;
    /* Optimisations GPU */
    transform: translateZ(0);
    -webkit-transform: translateZ(0);
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
}

:global(.sticky-header-component.scrolled .quotidien-link) {
    height: 0px;
}

@media (min-width: 769px) {
    :global(.sticky-header-component.scrolled .header__logo) {
        height: 51px !important;
        transform: scale(0.8) translateZ(0);
        margin-bottom: 0px !important;
        margin-top: 0px !important;
    }
    :global(.sticky-header-component.scrolled .header-wrapper) {
        padding: 8px 16px !important;
        transition: padding var(--header-transition); max-height: 67px;
    }
}

/* Mobile : optimisations spécifiques */
@media (max-width: 768px) {
    :global(.sticky-header-component .header__logo) {
        /* Transition plus rapide sur mobile */
        transition: transform 0.2s ease-out;
    }

    :global(.sticky-header-component.scrolled .header__logo) {
        transform: scale(0.8) translateZ(0);
        margin-bottom: 0px !important;
        margin-top: 0px !important;
    }

    /* Réduction des transitions complexes sur mobile */
    :global(.sticky-header-component.scrolled .header-wrapper) {
        padding: 8px 16px !important;
        transition: padding 0.2s ease-out;
    }
}

/* Container du logo pour contrôler l'espace global */
:global(.sticky-header-component .header__logo-container),
:global(.sticky-header-component .header__brand) {
    transition: padding var(--header-transition);
    will-change: padding;
    contain: layout;
}

:global(.sticky-header-component.scrolled .header__logo-container),
:global(.sticky-header-component.scrolled .header__brand) {
    padding-top: 4px !important;
    padding-bottom: 4px !important;
}

/* === MENU PRINCIPAL === */
:global(.sticky-header-component.scrolled #main-menu) {
    min-height: 37px !important;
    max-height: 37px !important;
    height: 37px !important;
    padding-top: 2px !important;
    padding-bottom: 2px !important;
    transition: all var(--header-transition);
    box-shadow: 0 12px 14px 0 rgba(0,0,0,.08);
    /* Optimisation GPU */
    will-change: height, padding;
    transform: translateZ(0);
}

:global(.sticky-header-component.scrolled nav#main-menu) {
    min-height: 37px !important;
    max-height: 37px !important;
    height: 37px !important;
    transition: height var(--header-transition);
    will-change: height;
}

/* === BOUTONS === */
:global(.sticky-header-component.scrolled .btn) {
    max-height: 30px;
    transition: max-height var(--header-transition);
    will-change: max-height;
}

/* === MENU SWIPER === */
:global(.sticky-header-component.scrolled .menu-swiper) {
    min-height: 34px !important;
    max-height: 34px !important;
    height: 34px !important;
    padding: 0;
    transition: height var(--header-transition);
    will-change: height;
    contain: layout;
}

:global(.sticky-header-component.scrolled .swiper-wrapper) {
    min-height: 34px !important;
    max-height: 34px !important;
    height: 34px !important;
    align-items: center !important;
    transition: height var(--header-transition);
    will-change: height;
}

:global(.sticky-header-component.scrolled .menu-swiper .menu-item) {
    padding: 2px 12px;
    transition: padding var(--header-transition);
    will-change: padding;
}
:global(.sticky-header-component.scrolled .menu-swiper .menu-item .menu-active .link-label:after) {
    bottom: -10px; height: 3px;
}

/* === TYPOGRAPHIE === */
:global(.sticky-header-component.scrolled .menu-item a) {
    font-size: 14px !important;
    transition: font-size var(--header-transition);
    will-change: font-size;
}

:global(.sticky-header-component.scrolled .grid > div:last-child .btn) {
    font-size: 13px !important;
    transition: font-size var(--header-transition);
    will-change: font-size;
}

:global(.sticky-header-component.scrolled .dropdown span) {
    font-size: 13px !important;
    transition: font-size var(--header-transition);
    will-change: font-size;
}

/* === ÉLÉMENTS CACHÉS === */
:global(.hidden-on-scrolled) {
    opacity: 1;
    transform: translateY(0) translateZ(0);
    visibility: visible;
    transition: opacity var(--header-transition), transform var(--header-transition), visibility var(--header-transition);
    will-change: opacity, transform, visibility;
    backface-visibility: hidden;
}

:global(.sticky-header-component.scrolled .hidden-on-scrolled) {
    opacity: 0;
    transform: translateY(-10px) translateZ(0);
    visibility: hidden;
    pointer-events: none
}

:global(.sticky-header-component.scrolled .hidden-on-scrolled.submenu) {
    display: none !important;
}

/* === ÉLÉMENTS VISIBLES SEULEMENT AU SCROLL === */
:global(.show-on-scrolled) {
    opacity: 0;
    transform: translateY(-10px) translateZ(0);
    visibility: hidden;
    pointer-events: none;
    transition: opacity var(--header-transition), transform var(--header-transition), visibility var(--header-transition);
    will-change: opacity, transform, visibility;
    backface-visibility: hidden;
}

:global(.sticky-header-component.scrolled .show-on-scrolled) {
    opacity: 1;
    transform: translateY(0) translateZ(0);
    visibility: visible;
    pointer-events: auto;
}

/* Optimisations spécifiques pour les appareils à faible performance */
@media (max-width: 768px) and (max-resolution: 150dpi) {
    :global(.sticky-header-component) {
        /* Simplification des transitions sur les appareils moins performants */
        transition: transform 0.15s ease-out !important;
    }

    :global(.sticky-header-component .header__logo) {
        transition: transform 0.15s ease-out !important;
    }

    :global(.hidden-on-scrolled) {
        transition: opacity 0.15s ease-out !important;
    }
}

/* Désactivation complète des animations si préférence utilisateur */
@media (prefers-reduced-motion: reduce) {
    :global(.sticky-header-component),
    :global(.sticky-header-component .header__logo),
    :global(.header-spacer),
    :global(.hidden-on-scrolled) {
        transition: none !important;
        animation: none !important;
    }
}
</style>
